/**
 * Confirm Email Page Component
 * Handles email confirmation when user clicks the link from their email
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Result, Button, Spin, Typography, Card } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, MailOutlined } from '@ant-design/icons';
import { confirmEmail } from '../../services/authService';
import PagesHeader from '../../PagesHeader';
import styles from './ConfirmEmail.module.css';

const { Text } = Typography;

/**
 * Confirm Email page - confirms user email when clicking link from email
 */
const ConfirmEmail = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  
  const [confirming, setConfirming] = useState(true);
  const [success, setSuccess] = useState(false);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);
  const [error, setError] = useState(null);

  // Get token and userId from URL params
  const token = searchParams.get('token');
  const userId = searchParams.get('userId');

  // Confirm email on component mount
  useEffect(() => {
    const doConfirmEmail = async () => {
      // Check if URL params are present
      if (!token || !userId) {
        setError(t('auth.invalidConfirmEmailLink', 'Invalid confirmation link. Please check your email for the correct link.'));
        setConfirming(false);
        return;
      }

      try {
        const response = await confirmEmail(userId, token);
        
        if (response.message?.includes('already confirmed')) {
          setAlreadyConfirmed(true);
        } else {
          setSuccess(true);
        }
      } catch (err) {
        setError(err.message || t('auth.confirmEmailFailed', 'Failed to confirm email. The link may have expired.'));
      } finally {
        setConfirming(false);
      }
    };

    doConfirmEmail();
  }, [token, userId, t]);

  // Loading state
  if (confirming) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <div className={styles.contentWrapper}>
          <Card className={styles.confirmCard}>
            <div className={styles.loadingContainer}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
              <Text className={styles.loadingText}>
                {t('auth.confirmingEmail', 'Confirming your email address...')}
              </Text>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <div className={styles.contentWrapper}>
          <Card className={styles.confirmCard}>
            <Result
              icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              status="success"
              title={t('auth.emailConfirmed', 'Email Confirmed!')}
              subTitle={t('auth.emailConfirmedMessage', 'Your email address has been confirmed. Your account is now pending administrator approval. You will receive an email once your account has been approved.')}
              extra={[
                <Link key="login" to="/login">
                  <Button type="primary" size="large" icon={<MailOutlined />}>
                    {t('auth.goToLogin', 'Go to Sign In')}
                  </Button>
                </Link>
              ]}
            />
          </Card>
        </div>
      </div>
    );
  }

  // Already confirmed state
  if (alreadyConfirmed) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <div className={styles.contentWrapper}>
          <Card className={styles.confirmCard}>
            <Result
              icon={<CheckCircleOutlined style={{ color: '#1890ff' }} />}
              status="info"
              title={t('auth.emailAlreadyConfirmed', 'Email Already Confirmed')}
              subTitle={t('auth.emailAlreadyConfirmedMessage', 'Your email address was already confirmed. Please sign in or wait for administrator approval.')}
              extra={[
                <Link key="login" to="/login">
                  <Button type="primary" size="large">
                    {t('auth.goToLogin', 'Go to Sign In')}
                  </Button>
                </Link>
              ]}
            />
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className={styles.pageContainer}>
      <PagesHeader />
      <div className={styles.contentWrapper}>
        <Card className={styles.confirmCard}>
          <Result
            icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            status="error"
            title={t('auth.confirmEmailFailedTitle', 'Confirmation Failed')}
            subTitle={error || t('auth.confirmEmailFailed', 'Failed to confirm email. The link may have expired.')}
            extra={[
              <Link key="login" to="/login">
                <Button type="primary" size="large">
                  {t('auth.goToLogin', 'Go to Sign In')}
                </Button>
              </Link>,
              <Link key="register" to="/register">
                <Button size="large">
                  {t('auth.createNewAccount', 'Create New Account')}
                </Button>
              </Link>
            ]}
          />
        </Card>
      </div>
    </div>
  );
};

export default ConfirmEmail;
